import { Slot } from 'primitives/slot'
import * as React from 'react'
import { tw } from 'utils/classnames'

/**
 *
 * Icon-holder that is used for smaller icons.
 * This one doesn't have background and margins and it's holding only icon. Used for smaller elements.
 *
 * Usage:
 *
 * <IconHolder size='small'>
 *  <LockUnlocked03Icon />
 * </IconHolder>
 *
 * Note that the size of the icon is handled for you, you only need to pass the icon as a child.
 * Do not add size, width, or height classes on IconHolder or its child.
 *
 * References:
 * https://creativefabrica.atlassian.net/browse/STU-3388?focusedCommentId=147148
 */

/**
 * Size options for IconHolder component
 *
 * @typedef {'small' | 'medium' | 'large'} IconHolderSize
 */
export type IconHolderSize =
  /** 12x12px (h-3 w-3) */
  | 'xsmall'
  /** 16x16px (h-4 w-4) */
  | 'small'
  /** 20x20px (h-5 w-5) */
  | 'medium'
  /** 24x24px (h-6 w-6) */
  | 'large'

interface IconHolderProps extends React.ComponentProps<'svg'> {
  size?: IconHolderSize
}

export const IconHolder = function IconHolder({
  ref,
  size = 'small',
  ...props
}: IconHolderProps & {
  ref?: React.RefObject<SVGSVGElement>
}) {
  const Component = Slot as unknown as 'svg'
  const hasAccessibleName = Boolean(props['aria-label'] ?? props['aria-labelledby'])

  return (
    <Component
      role={hasAccessibleName ? 'img' : undefined}
      aria-hidden={hasAccessibleName ? undefined : true}
      {...props}
      className={tw(iconHolderClasses.sizes[size], props.className)}
      ref={ref}
    />
  )
}

const iconHolderClasses = {
  sizes: {
    xsmall: 'size-3',
    small: 'size-4',
    medium: 'size-5',
    large: 'size-6',
  },
}
